import { Paragraph, PromotionCard } from '@purpurds/purpur';
import { TeliaCol, TeliaCtaLink, TeliaRow } from '@teliads/components/react';
import { useTranslation } from 'react-i18next';

import CardContentWrapper from '../card-content-wrapper/CardContentWrapper';
import Carousel from '../carousel/Carousel';
import { CarouselVariant } from '../carousel/types';

import { renderRichText } from 'utils/rich-text-render';
import { TruncatedTextByWords, createMultipleSizeImages } from 'utils/utils';

import './BottomCardsCardDisplay.scss';

interface BottomCardItem {
  title: string;
  body: string;
  tagline?: string;
  url: string;
  image: {
    url: string;
    altText: string;
    focusArea?: string;
  };
}

interface BottomCardsCardDisplayProps {
  items: BottomCardItem[];
  title?: string;
  preamble?: any;
  cta?: {
    text: string;
    url: string;
  };
  titleCentered?: boolean;
  isCarousel?: boolean;
  backgroundColor?: string;
}

const BottomCardsCardDisplay = ({
  items,
  title,
  preamble,
  cta,
  titleCentered = false,
  isCarousel = false,
  backgroundColor = '#FFFFFF',
}: BottomCardsCardDisplayProps) => {
  const { t } = useTranslation();
  return (
    <section className="bottom-card-section" style={{ backgroundColor: backgroundColor }}>
      <CardContentWrapper
        title={title}
        preamble={preamble ? renderRichText(preamble.json) : undefined}
        cta={cta}
        isTitleCentered={titleCentered}
      >
        <>
          {!isCarousel && (
            <TeliaRow>
              {items.map((item: any, id: number) => {
                const imageSizes = createMultipleSizeImages({
                  url: item.image.url,
                  altText: item.image.altText,
                  focusArea: item.image.focusArea,
                });
                return (
                  <TeliaCol
                    width={12}
                    widthLg={items.length > 3 ? 3 : 4}
                    widthMd={6}
                    key={`${item.title}-${id}`}
                    className="bottom-promotion-card__wrapper"
                  >
                    <PromotionCard
                      variant="primary"
                      title={TruncatedTextByWords(item.title, 6)}
                      key={item.title}
                      hasGradient
                      decorativeImage={
                        <img
                          className="bottom-promotion-card__image"
                          src={item.image.url ? imageSizes.medium : '/image/purple-900.png'}
                          alt={imageSizes.altText}
                        />
                      }
                      className="solutions-page__bottom-promotion-card"
                    >
                      <div className="bottom-promotion-card__text-wrapper">
                        <Paragraph className="bottom-promotion-card__preamble">
                          {item.body}
                        </Paragraph>
                        <TeliaCtaLink variant="primary-white" fullWidth href={item.url}>
                          {item.tagline || t('read_more')}
                        </TeliaCtaLink>
                      </div>
                    </PromotionCard>
                  </TeliaCol>
                );
              })}
            </TeliaRow>
          )}
          {isCarousel && (
            <Carousel variant={CarouselVariant.SMALL_FRAME}>
              {items.map((item: any) => {
                const imageSizes = createMultipleSizeImages({
                  url: item.image.url,
                  altText: item.image.altText,
                  focusArea: item.image.focusArea,
                });
                return (
                  <PromotionCard
                    variant="primary"
                    title={TruncatedTextByWords(item.title, 6)}
                    key={item.title}
                    hasGradient
                    decorativeImage={
                      <img
                        className="bottom-promotion-card__image"
                        src={item.image.url ? imageSizes.medium : '/image/purple-900.png'}
                        alt={imageSizes.altText}
                      />
                    }
                    className="solutions-page__bottom-promotion-card"
                  >
                    <div className="bottom-promotion-card__text-wrapper">
                      <Paragraph className="bottom-promotion-card__preamble">{item.body}</Paragraph>
                      <TeliaCtaLink variant="primary-white" fullWidth href={item.url}>
                        {item.tagline || t('read_more')}
                      </TeliaCtaLink>
                    </div>
                  </PromotionCard>
                );
              })}
            </Carousel>
          )}
        </>
      </CardContentWrapper>
    </section>
  );
};

export default BottomCardsCardDisplay;
